import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Howl } from 'howler';
import { FaGuitar, FaVolumeUp, FaMusic } from 'react-icons/fa';

const Guitar: React.FC = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentString, setCurrentString] = useState<number | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const guitarRef = useRef<HTMLDivElement>(null);
  const soundRefs = useRef<Howl[]>([]);

  // Initialize guitar string sounds
  useEffect(() => {
    // Guitar string sounds (from low E to high E)
    const stringNotes = [
      '/sounds/guitar-e2.mp3',
      '/sounds/guitar-a2.mp3',
      '/sounds/guitar-d3.mp3',
      '/sounds/guitar-g3.mp3',
      '/sounds/guitar-b3.mp3',
      '/sounds/guitar-e4.mp3',
    ];

    // Create Howl instances for each string
    soundRefs.current = stringNotes.map(note => 
      new Howl({
        src: [note],
        volume: 0.7,
        onend: () => {
          setCurrentString(null);
        }
      })
    );

    return () => {
      // Stop all sounds when component unmounts
      soundRefs.current.forEach(sound => sound.stop());
    };
  }, []);

  // Play a guitar string sound
  const playString = (stringIndex: number) => {
    if (stringIndex >= 0 && stringIndex < soundRefs.current.length) {
      // Stop any currently playing sounds
      soundRefs.current.forEach(sound => sound.stop());
      
      // Play the selected string sound
      soundRefs.current[stringIndex].play();
      setCurrentString(stringIndex);
    }
  };

  // Handle mouse movement over the guitar
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!guitarRef.current || !isHovering) return;
    
    const rect = guitarRef.current.getBoundingClientRect();
    const relativeY = e.clientY - rect.top;
    const relativeX = e.clientX - rect.left;
    const height = rect.height;
    
    // Update mouse position for glow effect
    setMousePosition({ x: relativeX, y: relativeY });
    
    // Determine which string is being hovered based on Y position
    const stringIndex = Math.floor((relativeY / height) * 6);
    
    // Only play if it's a different string than the current one
    if (stringIndex !== currentString && stringIndex >= 0 && stringIndex < 6) {
      playString(stringIndex);
    }
  };

  return (
    <section className="py-24 px-6 relative overflow-hidden bg-black">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Red Gradient Orbs */}
        <motion.div 
          className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-br from-red-500/20 to-red-700/20 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-red-600/15 to-red-800/15 rounded-full blur-3xl"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.4, 0.7, 0.4]
          }}
          transition={{ 
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
        
        {/* Floating Musical Notes */}
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-red-500/10 pointer-events-none"
            initial={{ 
              x: `${Math.random() * 100}%`, 
              y: `${Math.random() * 100}%`,
              rotate: Math.random() * 360,
              scale: Math.random() * 0.5 + 0.5
            }}
            animate={{ 
              y: [null, `${Math.random() * 100}%`],
              rotate: [null, Math.random() * 360 + 180],
              opacity: [0.1, 0.3, 0.1]
            }}
            transition={{ 
              duration: Math.random() * 20 + 15, 
              repeat: Infinity,
              ease: "linear"
            }}
            style={{ 
              fontSize: `${Math.random() * 3 + 2}rem`,
            }}
          >
            {['♪', '♫', '♬', '♩', '♭', '♮', '♯'][Math.floor(Math.random() * 7)]}
          </motion.div>
        ))}

        {/* Subtle Grid Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="w-full h-full bg-grid-pattern"></div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-red-400 via-red-300 to-white bg-clip-text text-transparent">
              Experience the English Guitar
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-white/80 max-w-4xl mx-auto leading-relaxed">
            Hover over the guitar to play different strings and immerse yourself in the sound of English music
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Guitar Visualization - Repositioned to Left */}
          <motion.div 
            className="relative order-2 lg:order-1"
            initial={{ opacity: 0, x: -100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <div 
              ref={guitarRef}
              className="relative w-[400px] h-[500px] mx-auto cursor-pointer group"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => {
                setIsHovering(false);
                setCurrentString(null);
                soundRefs.current.forEach(sound => sound.stop());
              }}
              onMouseMove={handleMouseMove}
            >
              {/* Interactive Glow Effect */}
              {isHovering && (
                <motion.div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `radial-gradient(circle 150px at ${mousePosition.x}px ${mousePosition.y}px, rgba(239, 68, 68, 0.3) 0%, rgba(239, 68, 68, 0.1) 40%, transparent 70%)`
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
              )}

              {/* Guitar Body with Enhanced Styling */}
              <motion.div 
                className="absolute w-[320px] h-[320px] bg-gradient-to-br from-amber-800 via-amber-900 to-amber-950 rounded-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 shadow-2xl border-4 border-amber-700/50"
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 25px 50px rgba(0, 0, 0, 0.5), 0 0 50px rgba(239, 68, 68, 0.3)"
                }}
                transition={{ duration: 0.3 }}
              >
                {/* Sound Hole */}
                <div className="absolute w-[120px] h-[120px] bg-black rounded-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 border-4 border-amber-600/30">
                  {/* Sound Hole Details */}
                  <div className="absolute inset-4 border border-amber-600/20 rounded-full"></div>
                  <div className="absolute inset-8 border border-amber-600/10 rounded-full"></div>
                </div>
                
                {/* Body Decorations */}
                <div className="absolute top-8 left-1/2 transform -translate-x-1/2 w-16 h-2 bg-amber-600/30 rounded-full"></div>
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 w-20 h-2 bg-amber-600/30 rounded-full"></div>
              </motion.div>
              
              {/* Guitar Neck with Enhanced Details */}
              <motion.div 
                className="absolute w-[60px] h-[400px] bg-gradient-to-b from-amber-700 via-amber-800 to-amber-900 top-0 left-1/2 transform -translate-x-1/2 rounded-t-xl border-2 border-amber-600/30"
                whileHover={{ 
                  scaleX: 1.1,
                  boxShadow: "0 0 30px rgba(239, 68, 68, 0.4)"
                }}
                transition={{ duration: 0.3 }}
              >
                {/* Frets with Enhanced Styling */}
                {[...Array(12)].map((_, i) => (
                  <motion.div 
                    key={i} 
                    className="absolute w-full h-1 bg-gradient-to-r from-gray-300 via-white to-gray-300 shadow-sm" 
                    style={{ top: `${8 + i * 7}%` }}
                    whileHover={{ scaleY: 1.5 }}
                    transition={{ duration: 0.2 }}
                  />
                ))}
                
                {/* Fret Markers with Glow */}
                {[3, 5, 7, 9].map((fret) => (
                  <motion.div 
                    key={fret}
                    className="absolute w-4 h-4 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full left-1/2 transform -translate-x-1/2 shadow-lg"
                    style={{ top: `${8 + fret * 7}%` }}
                    whileHover={{ 
                      scale: 1.3,
                      boxShadow: "0 0 15px rgba(239, 68, 68, 0.6)"
                    }}
                    transition={{ duration: 0.2 }}
                  />
                ))}
                
                {/* Double Dot at 12th Fret */}
                <div className="absolute top-[92%] left-1/2 transform -translate-x-1/2 flex space-x-2">
                  <div className="w-3 h-3 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full shadow-lg"></div>
                  <div className="w-3 h-3 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full shadow-lg"></div>
                </div>
              </motion.div>
              
              {/* Guitar Strings with Enhanced Effects */}
              {[...Array(6)].map((_, i) => (
                <motion.div 
                  key={i}
                  className={`absolute h-full left-1/2 transform -translate-x-1/2 transition-all duration-200 ${
                    currentString === i 
                      ? 'bg-gradient-to-b from-red-300 via-red-400 to-red-300 shadow-red-400' 
                      : 'bg-gradient-to-b from-gray-300 via-gray-400 to-gray-300'
                  }`}
                  style={{ 
                    left: `${42 + i * 3}%`,
                    width: `${1.5 + (5-i) * 0.5}px`,
                    filter: currentString === i ? 'drop-shadow(0 0 8px rgba(239, 68, 68, 0.8))' : 'none',
                    boxShadow: currentString === i ? '0 0 20px rgba(239, 68, 68, 0.5)' : 'none'
                  }}
                  whileHover={{ 
                    scaleX: 1.2,
                    filter: 'drop-shadow(0 0 10px rgba(239, 68, 68, 0.6))'
                  }}
                  transition={{ duration: 0.2 }}
                />
              ))}
              
              {/* String Vibration Effect - Enhanced */}
              {currentString !== null && (
                <motion.div
                  className="absolute h-full left-1/2 transform -translate-x-1/2 z-10 pointer-events-none"
                  style={{ 
                    left: `${42 + currentString * 3}%`,
                    width: '6px',
                  }}
                  initial={{ scaleY: 1, opacity: 0.8 }}
                  animate={{ 
                    scaleY: [1, 1.05, 0.95, 1.02, 0.98, 1],
                    scaleX: [1, 1.3, 0.7, 1.1, 0.9, 1],
                    opacity: [0.8, 1, 0.6, 0.8, 0.4, 0]
                  }}
                  transition={{ duration: 1.2, ease: "easeOut" }}
                >
                  <div className="w-full h-full bg-gradient-to-b from-red-400 via-red-300 to-red-400 blur-sm rounded-full"></div>
                </motion.div>
              )}
              
              {/* Sound Waves Effect */}
              {currentString !== null && (
                <>
                  {[...Array(3)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 border-2 border-red-400/40 rounded-full pointer-events-none"
                      initial={{ width: 0, height: 0, opacity: 0.8 }}
                      animate={{ 
                        width: ['0px', '200px', '400px'], 
                        height: ['0px', '200px', '400px'],
                        opacity: [0.8, 0.4, 0]
                      }}
                      transition={{ 
                        duration: 2,
                        delay: i * 0.3,
                        ease: "easeOut"
                      }}
                    />
                  ))}
                </>
              )}
              
              {/* Sound Indicator with Enhanced Animation */}
              {currentString !== null && (
                <motion.div
                  className="absolute -top-8 -right-8 bg-gradient-to-br from-red-500/30 to-red-700/30 backdrop-blur-lg rounded-full p-4 border border-red-400/30"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  exit={{ scale: 0, rotate: 180 }}
                  transition={{ duration: 0.5, ease: "backOut" }}
                >
                  <FaVolumeUp className="text-red-400 w-6 h-6 animate-pulse" />
                  <motion.div
                    className="absolute inset-0 border-2 border-red-400/50 rounded-full"
                    animate={{ scale: [1, 1.3, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                </motion.div>
              )}

              {/* Guitar Headstock */}
              <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 w-16 h-20 bg-gradient-to-b from-amber-700 to-amber-800 rounded-t-lg border-2 border-amber-600/30">
                {/* Tuning Pegs */}
                {[...Array(6)].map((_, i) => (
                  <div 
                    key={i}
                    className={`absolute w-3 h-3 bg-gradient-to-br from-gray-300 to-gray-500 rounded-full shadow-lg ${
                      i % 2 === 0 ? 'left-1' : 'right-1'
                    }`}
                    style={{ top: `${15 + i * 12}%` }}
                  />
                ))}
              </div>

              {/* Interactive Glow Ring */}
              <motion.div
                className="absolute inset-0 rounded-full pointer-events-none"
                animate={isHovering ? {
                  boxShadow: [
                    "0 0 0 0 rgba(239, 68, 68, 0)",
                    "0 0 0 20px rgba(239, 68, 68, 0.1)",
                    "0 0 0 40px rgba(239, 68, 68, 0)"
                  ]
                } : {}}
                transition={{ duration: 1.5, repeat: isHovering ? Infinity : 0 }}
              />
            </div>
            
            {/* Enhanced Hover Instructions */}
            <motion.div 
              className="text-center mt-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
            >
              <div className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-lg rounded-full border border-white/20">
                <FaMusic className="text-red-400 mr-3 w-5 h-5" />
                <span className="text-white font-medium">Hover over the strings to play</span>
              </div>
              
              {isHovering && (
                <motion.p 
                  className="text-red-300 mt-4 font-medium"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  🎵 String {(currentString || 0) + 1} playing...
                </motion.p>
              )}
            </motion.div>
          </motion.div>
          
          {/* Content Section - Repositioned to Right */}
          <motion.div 
            className="order-1 lg:order-2 space-y-8"
            initial={{ opacity: 0, x: 100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
          >
            {/* Main Content Card */}
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
              <motion.div
                className="flex items-center mb-6"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                  <FaGuitar className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">The Heart of English Music</h3>
                  <p className="text-red-300 font-medium">Interactive Learning Experience</p>
                </div>
              </motion.div>
              
              <p className="text-white/90 text-lg leading-relaxed mb-8">
                The guitar is central to many English music genres, from folk to rock. 
                Learning English through music helps you connect with the culture and improves your 
                pronunciation and rhythm naturally.
              </p>

              {/* Feature List */}
              <div className="space-y-4">
                {[
                  { icon: '🎸', title: 'Interactive Guitar', desc: 'Play virtual strings with realistic sound' },
                  { icon: '🎵', title: 'Musical Learning', desc: 'Learn vocabulary through song lyrics' },
                  { icon: '🗣️', title: 'Pronunciation', desc: 'Improve accent with musical rhythm' },
                  { icon: '🎯', title: 'Cultural Context', desc: 'Understand English-speaking cultures' }
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-all duration-300"
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.6 + (index * 0.1) }}
                    whileHover={{ 
                      scale: 1.02,
                      boxShadow: "0 10px 25px rgba(239, 68, 68, 0.1)"
                    }}
                  >
                    <span className="text-2xl mr-4">{feature.icon}</span>
                    <div>
                      <h4 className="text-white font-semibold">{feature.title}</h4>
                      <p className="text-white/70 text-sm">{feature.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Vocabulary Card */}
            <motion.div 
              className="bg-gradient-to-br from-red-500/20 to-red-700/20 backdrop-blur-xl rounded-2xl p-6 border border-red-400/30 shadow-xl"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.8 }}
              whileHover={{ 
                scale: 1.02,
                boxShadow: "0 20px 40px rgba(239, 68, 68, 0.2)"
              }}
            >
              <h4 className="font-bold text-xl mb-4 flex items-center text-white">
                <FaGuitar className="text-red-400 mr-3 text-2xl" />
                English Guitar Vocabulary
              </h4>
              <div className="grid grid-cols-1 gap-4">
                {[
                  { english: 'The guitar', spanish: 'La guitarra' },
                  { english: 'The strings', spanish: 'Las cuerdas' },
                  { english: 'To play the guitar', spanish: 'Tocar la guitarra' },
                  { english: 'The rhythm', spanish: 'El ritmo' }
                ].map((vocab, index) => (
                  <motion.div 
                    key={index}
                    className="flex justify-between items-center p-3 bg-white/10 rounded-lg border border-white/10"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 1 + (index * 0.1) }}
                    whileHover={{ 
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                      scale: 1.02
                    }}
                  >
                    <span className="text-red-300 font-medium">{vocab.english}</span>
                    <span className="text-white/80">{vocab.spanish}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
      
      {/* Custom Styles */}
      <style jsx>{`
        .bg-grid-pattern {
          background-image: 
            linear-gradient(rgba(239, 68, 68, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(239, 68, 68, 0.05) 1px, transparent 1px);
          background-size: 40px 40px;
          animation: grid-drift 30s linear infinite;
        }
        
        @keyframes grid-drift {
          0% { transform: translate(0, 0); }
          100% { transform: translate(40px, 40px); }
        }
      `}</style>
    </section>
  );
};

export default Guitar;